import type { BillingEntity } from '../@types/BillingEntity';
import type { PaymentStatus } from '../@types/PaymentStatus';
import { JWTHeader } from '../utils/JWTHeader';

export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  paymentStatus: PaymentStatus | null;
  subscriptionEnd: string | null;
  lastPaymentDate: string | null;
}

export interface ProAbonoSubscription {
  [key: string]: unknown;
}

export interface ProAbonoSubscriptionsResponse {
  Results?: ProAbonoSubscription[];
}

export interface ProAbonoInvoice {
  [key: string]: unknown;
}

export interface ProAbonoInvoicesResultsResponse {
  Results?: ProAbonoInvoice[];
}

export interface ProAbonoInvoiceLink {
  rel: string;
  href?: string;
}

export interface ProAbonoInvoiceDetailed {
  Id: string;
  DateIssue: string;
  FullNumber: string;
  AmountTotal: number;
  Status: string;
  StateInvoice: string;
  TypeCredit?: string;
  Links?: ProAbonoInvoiceLink[];
}

export interface ProAbonoInvoicesResponse {
  Items?: ProAbonoInvoiceDetailed[];
  Results?: ProAbonoInvoiceDetailed[];
}

export interface SubscriptionDetails {
  company: {
    id: number;
    name: string;
    siren: string;
  };
  subscriptions: ProAbonoSubscriptionsResponse;
  invoices: ProAbonoInvoicesResponse;
  portalLink?: string;
  localStatus: {
    hasActiveSubscription: boolean;
    paymentStatus: PaymentStatus | null;
    subscriptionEnd: string | null;
  };
}

export interface CreateSubscriptionResponse {
  paymentLink: string | null;
  subscription: ProAbonoSubscription;
}

export interface OfferDetails {
  ReferenceOffer: string;
  Name: string;
  TitleLocalized: string;
  AmountRecurrence: number;
  DurationRecurrence: number;
  UnitRecurrence: string;
  IsVisible: boolean;
}

/**
 * Payment API Service
 * Handles all payment-related API calls using the new ProAbono system
 */
export const paymentApi = {
  /**
   * Get subscription status for current company
   */
  async getSubscriptionStatus(companyId: number, jwt: string): Promise<SubscriptionStatus> {
    const response = await fetch(`/api/companies/${companyId}/payment/subscription/status`, JWTHeader(jwt));
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get full subscription details including invoices
   */
  async getSubscription(companyId: number, jwt: string): Promise<SubscriptionDetails> {
    const response = await fetch(`/api/companies/${companyId}/payment/subscription`, JWTHeader(jwt));
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Create a new subscription (249€/month)
   * Returns payment link for user to complete payment
   */
  async createSubscription(companyId: number, jwt: string): Promise<CreateSubscriptionResponse> {
    const response = await fetch(`/api/companies/${companyId}/payment/subscription`, {
      ...JWTHeader(jwt),
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get the standard offer details (249€/month)
   */
  async getOffer(companyId: number, jwt: string): Promise<OfferDetails> {
    const response = await fetch(`/api/companies/${companyId}/payment/offer`, JWTHeader(jwt));
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },
};

/**
 * Admin Payment API Service
 * Admin-only endpoints for payment management
 */
export const paymentAdminApi = {
  /**
   * Get payment status for a specific company
   */
  async getCompanyPaymentStatus(companyId: number, jwt: string): Promise<Record<string, unknown>> {
    const response = await fetch(`/api/admin/payment/companies/${companyId}/status`, JWTHeader(jwt));
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get all subscriptions for a company from ProAbono
   */
  async getCompanySubscriptions(companyId: number, jwt: string): Promise<ProAbonoSubscription[]> {
    const response = await fetch(`/api/admin/payment/companies/${companyId}/subscriptions`, JWTHeader(jwt));
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get all invoices for a company from ProAbono
   */
  async getCompanyInvoices(companyId: number, jwt: string): Promise<ProAbonoInvoice[]> {
    const response = await fetch(`/api/admin/payment/companies/${companyId}/invoices`, JWTHeader(jwt));
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Create ProAbono customer for a company
   */
  async createCustomer(companyId: number, jwt: string): Promise<Record<string, unknown>> {
    const response = await fetch(`/api/admin/payment/companies/${companyId}/customers`, {
      ...JWTHeader(jwt),
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get ProAbono payment link for a company (Admin)
   */
  async getPaymentLink(companyId: number, jwt: string): Promise<{ paymentLink: string | null }> {
    const response = await fetch(`/api/admin/payment/companies/${companyId}/payment-link`, {
      ...JWTHeader(jwt),
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Update payment status for a company (Admin override)
   */
  async updatePaymentStatus(
    companyId: number,
    paymentStatus: PaymentStatus,
    jwt: string,
  ): Promise<{ company?: unknown; payment?: { paymentStatus?: PaymentStatus; subscriptionEnd?: string | null } }> {
    const response = await fetch(`/api/admin/payment/companies/${companyId}/status`, {
      ...JWTHeader(jwt),
      method: 'PATCH',
      body: JSON.stringify({ paymentStatus }),
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },

  /**
   * Update the entity billing a company (Admin), resetting its ProAbono customer link
   */
  async updateBillingEntity(
    companyId: number,
    billingEntity: BillingEntity,
    jwt: string,
  ): Promise<{
    message?: string;
    company?: { id: number; name: string; siren: string };
    payment?: {
      hasActiveSubscription?: boolean;
      paymentStatus?: PaymentStatus | null;
      billingEntity?: BillingEntity;
      proAbonoReference?: string | null;
      subscriptionEnd?: string | null;
      lastPaymentDate?: string | null;
      createDate?: string | null;
    };
  }> {
    const response = await fetch(`/api/admin/payment/companies/${companyId}/billing-entity`, {
      ...JWTHeader(jwt),
      method: 'PATCH',
      body: JSON.stringify({ billingEntity }),
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },
};

